// packages/web-ui/src/panels/chat-panel/ReconnectingBanner.tsx
//
// W2-S5 AC #8 — Inline SSE-drop banner. Visible while the SSE connection
// is `reconnecting` or `disconnected`. The banner is sticky at the bottom
// of the chat panel (above the composer placeholder) — NOT a transient
// toast (Pitfall #18).

import type { JSX } from 'react';
import { Loader2 } from 'lucide-react';
import type { SseConnectionState } from '../../state/sse.js';

export interface ReconnectingBannerProps {
  state: SseConnectionState;
  reconnectAttempts: number;
  onRetry: () => void;
}

export function ReconnectingBanner(props: ReconnectingBannerProps): JSX.Element | null {
  if (props.state === 'connected' || props.state === 'connecting') {
    return null;
  }
  return (
    <div
      role="alert"
      aria-live="assertive"
      data-testid="reconnecting-banner"
      data-state={props.state}
      className="sticky bottom-0 z-10 flex items-center gap-2 border-y border-accent-amber bg-accent-amber-bg px-3 py-2 text-sm text-accent-amber"
    >
      <Loader2
        size={16}
        className="motion-safe:animate-spin motion-reduce:animate-none"
        aria-hidden="true"
      />
      {props.state === 'disconnected' ? (
        <>
          <span data-testid="reconnecting-banner-text">Disconnected. Tap Retry to reconnect.</span>
          <button
            type="button"
            data-testid="reconnecting-banner-retry"
            onClick={props.onRetry}
            className="ml-auto rounded border border-accent-amber px-2 py-0.5 hover:bg-accent-amber/10"
          >
            Retry
          </button>
        </>
      ) : (
        <span data-testid="reconnecting-banner-text">
          Reconnecting… (attempt {props.reconnectAttempts})
        </span>
      )}
    </div>
  );
}
